import { computed, ref, watch } from "vue";
import i18n from "@/i18n";
import { createGlobalState } from "@vueuse/core";
import useApplicationsState from "./applicationsState";
import { usePromiseWrapper, useReadonlyWithSetter, useResponsive } from "@/composables/common";
import { ApplicationsHttpService } from "@/api/http/ApplicationsHttpService";
import type { DocumentOptions, PendingUpload, UploadSource } from "@/types/DocumentTypes";
import { makeRandomString } from "@/util/common";
import { ApplicationPrivateChannel } from "@/api/ws/channels/ApplicationPrivateChannel";
import type {
  Document,
  RequiredDocument,
  UpdateDocumentPayload,
} from "@/api/http/schemas/documentsSchemas";
import useToastState from "@/composables/state/toastState";
import { FILE_TYPE_OTHER } from "@/constants/files";
import { DOCUMENT_PROCESSING_TIMEOUT_MS } from "@/constants/documents";
import { isValidChatFileSize, isValidChatFileType } from "@/util/file";
import { useAnalyticsState } from "@/composables/state/analyticsState";
import { AnalyticsEvent } from "@/enums/analytics";
import CommentSavedForFileEvent from "@/api/ws/events/CommentSavedForFileEvent";
import FileTypeChangedEvent from "@/api/ws/events/FileTypeChanged";
import {
  buildDocumentRows,
  countDocumentsNeedingAction,
  deriveDocumentStatus,
  isAwaitingCategorization,
  requiredDocumentsForCheckoutStep,
} from "@/util/documents";

const { t } = i18n.global;

type UploadFailure =
  | { kind: "upload"; fileType: Document["file_type"]; batchSequence: number }
  | { kind: "replace"; replacedDocumentId: Document["id"] };

type FetchRequiredDocumentsParams = {
  dealIdOverride?: string;
  params?: Record<string, unknown>;
};

type BatchOutcome = {
  uploaded: Document[];
  failed: PendingUpload[];
  uploadSource: UploadSource;
};

type UploadDocumentsParams = {
  files: FileList | File[];
  documentOptions?: DocumentOptions;
  uploadSource?: UploadSource;
  replacedDocumentId?: Document["id"];
};

type ReplaceDocumentParams = {
  documentId: Document["id"];
  files: FileList;
  documentOptions?: DocumentOptions;
  uploadSource?: UploadSource;
};

type UploadSingleFileParams = {
  uploadApplicationId: string;
  uploadGeneration: number;
  batchSequence: number;
  pendingUpload: PendingUpload;
  documentOptions?: DocumentOptions;
  replacedDocumentId?: Document["id"];
};

type ClearSupersededUploadFailuresParams = {
  fileType: Document["file_type"];
  batchSequence: number;
};

type DocumentsNeedingActionCountForStepParams = {
  stepId: string;
  hasOwnStips: boolean;
};

const sortRequiredDocuments = (requiredDocuments: RequiredDocument[]) =>
  [...requiredDocuments].sort((a, b) => {
    const isCompleteA = a.uploaded_count >= a.required_count;
    const isCompleteB = b.uploaded_count >= b.required_count;

    if (isCompleteA !== isCompleteB) {
      return isCompleteA ? 1 : -1;
    }

    return (a.weight ?? 0) - (b.weight ?? 0);
  });

export const useDocumentsState = createGlobalState(() => {
  const toast = useToastState();
  const { selectedApplication } = useApplicationsState();
  const analytics = useAnalyticsState();
  const { isMobile } = useResponsive();

  const defaultErrorHandler = (action: "get" | "upload" | "update" | "delete") => {
    toast.value.addMessage({
      title: t(`documents.errors.${action}`),
      type: "error",
    });
  };

  const [applicationId, setApplicationId] = useReadonlyWithSetter<string | null>(null);
  const [uploadedDocuments, setUploadedDocuments] = useReadonlyWithSetter<Document[]>([]);
  const [requiredDocuments, setRequiredDocuments] = useReadonlyWithSetter<RequiredDocument[]>([]);
  const [checkoutRequiredDocuments, setCheckoutRequiredDocuments] = useReadonlyWithSetter<
    RequiredDocument[]
  >([]);

  const pendingUploads = ref<PendingUpload[]>([]);
  const processingDocumentIds = ref<Set<Document["id"]>>(new Set());
  const uploadFailures = ref<UploadFailure[]>([]);
  const processingTimers = new Map<Document["id"], ReturnType<typeof setTimeout>>();
  const trackedBatches = new Map<string, BatchOutcome>();
  const documentBatchSequences = new Map<Document["id"], number>();
  let isTrackingCheckoutRequiredDocuments = false;
  let latestBatchSequence = 0;
  // Bumped synchronously on every application switch, so A → B → A still invalidates in-flight work.
  let applicationGeneration = 0;

  const isApplicationSwitchedSince = (generation: number) => generation !== applicationGeneration;

  const applicationChannel = ref<ApplicationPrivateChannel | null>(null);

  const fetchRequiredDocuments = async ({
    dealIdOverride,
    params,
  }: FetchRequiredDocumentsParams) => {
    const dealId = dealIdOverride ?? applicationId.value;
    if (!dealId) {
      return;
    }

    const requestGeneration = applicationGeneration;
    const requiredDocuments = await new ApplicationsHttpService().getRequiredDocuments(
      dealId,
      params
    );

    if (isApplicationSwitchedSince(requestGeneration)) {
      return;
    }

    return sortRequiredDocuments(requiredDocuments);
  };

  const { executeFetch: getRequiredDocuments } = usePromiseWrapper(
    async (dealIdOverride?: string) => {
      const requiredDocuments = await fetchRequiredDocuments({ dealIdOverride });

      if (requiredDocuments) {
        setRequiredDocuments(requiredDocuments);
      }
    },
    { onError: () => defaultErrorHandler("get") }
  );

  const { executeFetch: getCheckoutRequiredDocuments } = usePromiseWrapper(
    async (dealIdOverride?: string) => {
      isTrackingCheckoutRequiredDocuments = true;

      const checkoutRequiredDocuments = await fetchRequiredDocuments({
        dealIdOverride,
        params: { offer_stips_only: 1 },
      });

      if (checkoutRequiredDocuments) {
        setCheckoutRequiredDocuments(checkoutRequiredDocuments);
      }
    },
    { onError: () => defaultErrorHandler("get") }
  );

  const refreshRequiredDocuments = async (dealIdOverride?: string) => {
    if (!isTrackingCheckoutRequiredDocuments) {
      await getRequiredDocuments(dealIdOverride);
      return;
    }

    await Promise.all([
      getRequiredDocuments(dealIdOverride),
      getCheckoutRequiredDocuments(dealIdOverride),
    ]);
  };

  const { executeFetch: getUploadedDocuments } = usePromiseWrapper(
    async (dealIdOverride?: string) => {
      const dealId = dealIdOverride ?? applicationId.value;
      if (!dealId) {
        return;
      }

      const requestGeneration = applicationGeneration;
      const uploadedDocuments = await new ApplicationsHttpService().getUploadedDocuments(dealId, {
        include_content: 1,
      });

      if (isApplicationSwitchedSince(requestGeneration)) {
        return;
      }

      setUploadedDocuments(uploadedDocuments);
    },
    { onError: () => defaultErrorHandler("get") }
  );

  const clearUploadFailures = (shouldClear: (failure: UploadFailure) => boolean) => {
    const remainingFailures = uploadFailures.value.filter((failure) => !shouldClear(failure));

    if (remainingFailures.length === uploadFailures.value.length) {
      return false;
    }

    uploadFailures.value = remainingFailures;

    return true;
  };

  const clearSupersededUploadFailures = ({
    fileType,
    batchSequence,
  }: ClearSupersededUploadFailuresParams) =>
    clearUploadFailures(
      (failure) =>
        failure.kind === "upload" &&
        failure.fileType === fileType &&
        failure.batchSequence < batchSequence
    );

  const filterUploadableFiles = (files: File[]) => {
    const unsupported: File[] = [];
    const oversized: File[] = [];
    const uploadable: File[] = [];

    for (const file of files) {
      if (!isValidChatFileType(file)) {
        unsupported.push(file);
        continue;
      }

      if (!isValidChatFileSize(file.size)) {
        oversized.push(file);
        continue;
      }

      uploadable.push(file);
    }

    if (unsupported.length) {
      toast.value.addMessage({
        title: t("documents.errors.invalidFiles", {
          names: unsupported.map((file) => file.name).join(", "),
        }),
        type: "error",
      });
    }

    if (oversized.length) {
      toast.value.addMessage({
        title: t("documents.errors.oversizedFiles", {
          names: oversized.map((file) => file.name).join(", "),
        }),
        type: "error",
      });
    }

    return uploadable;
  };

  const resolveDocumentProcessing = (documentId: Document["id"]) => {
    const timer = processingTimers.get(documentId);

    if (timer) {
      clearTimeout(timer);
      processingTimers.delete(documentId);
    }

    if (!processingDocumentIds.value.has(documentId)) {
      return;
    }

    const next = new Set(processingDocumentIds.value);
    next.delete(documentId);
    processingDocumentIds.value = next;
  };

  // The categorization event can be dropped or arrive late, so refresh before showing a final state.
  const expireDocumentProcessing = async (documentId: Document["id"]) => {
    if (!processingDocumentIds.value.has(documentId)) {
      return;
    }

    await Promise.all([getUploadedDocuments(), refreshRequiredDocuments()]);
    resolveDocumentProcessing(documentId);
  };

  const trackDocumentProcessing = (documentId: Document["id"]) => {
    const next = new Set(processingDocumentIds.value);
    next.add(documentId);
    processingDocumentIds.value = next;

    processingTimers.set(
      documentId,
      setTimeout(() => expireDocumentProcessing(documentId), DOCUMENT_PROCESSING_TIMEOUT_MS)
    );
  };

  const finalizeBatch = (batchId: string) => {
    const batch = trackedBatches.get(batchId);
    if (!batch) {
      return;
    }

    const stillUploading = pendingUploads.value.some((pending) => pending.batchId === batchId);
    if (stillUploading) {
      return;
    }

    const stillProcessing = batch.uploaded.some((document) =>
      processingDocumentIds.value.has(document.id)
    );
    if (stillProcessing) {
      return;
    }

    trackedBatches.delete(batchId);

    // The upload response predates categorization, so read each document back from current state.
    const settled = batch.uploaded.map(
      (uploaded) =>
        uploadedDocuments.value.find((document) => document.id === uploaded.id) ?? uploaded
    );

    const uploadedDetected = settled.filter((doc) => doc.file_type !== FILE_TYPE_OTHER);
    const uploadedUndetected = settled.filter((doc) => doc.file_type === FILE_TYPE_OTHER);

    if (uploadedDetected.length === 1) {
      toast.value.addMessage({
        title: t("documents.toasts.uploadedSingle", { name: uploadedDetected[0].original_name }),
        type: "success",
      });
    } else if (uploadedDetected.length > 1) {
      toast.value.addMessage({
        title: t("documents.toasts.uploadedMultiple", { count: uploadedDetected.length }),
        type: "success",
      });
    }

    if (uploadedUndetected.length === 1) {
      toast.value.addMessage({
        title: t("documents.toasts.undetectedSingle", {
          name: uploadedUndetected[0].original_name,
        }),
        type: "warning",
      });
    } else if (uploadedUndetected.length > 1) {
      toast.value.addMessage({
        title: t("documents.toasts.undetectedMultiple", { count: uploadedUndetected.length }),
        type: "warning",
      });
    }

    if (settled.length) {
      analytics.trackEvent(AnalyticsEvent.DocumentUploaded, {
        application_id: applicationId.value,
        upload_source: batch.uploadSource,
        is_mobile: isMobile.value,
        file_count: settled.length,
        documents: settled.map(({ file_type, file_size, original_name }) => ({
          file_type,
          file_size,
          original_name,
        })),
      });
    }

    if (batch.failed.length) {
      analytics.trackEvent(AnalyticsEvent.DocumentUploadFailed, {
        application_id: applicationId.value,
        error_type: "upload_error",
        file_count: batch.failed.length,
        documents: batch.failed.map(({ fileName, fileSize, fileType }) => ({
          file_type: fileType,
          file_size: fileSize,
          original_name: fileName,
        })),
      });
    }
  };

  const uploadSingleFile = async ({
    uploadApplicationId,
    uploadGeneration,
    batchSequence,
    pendingUpload,
    documentOptions,
    replacedDocumentId,
  }: UploadSingleFileParams) => {
    const batch = trackedBatches.get(pendingUpload.batchId);

    const removePending = () => {
      pendingUploads.value = pendingUploads.value.filter(
        (candidate) => candidate.id !== pendingUpload.id
      );
    };

    try {
      const [uploadedDocument] = await new ApplicationsHttpService().addDocuments(
        uploadApplicationId,
        [{ file: pendingUpload.file, ...documentOptions }]
      );

      if (isApplicationSwitchedSince(uploadGeneration)) {
        return null;
      }

      removePending();
      setUploadedDocuments([uploadedDocument, ...uploadedDocuments.value]);
      documentBatchSequences.set(uploadedDocument.id, batchSequence);
      clearSupersededUploadFailures({ fileType: uploadedDocument.file_type, batchSequence });

      batch?.uploaded.push(uploadedDocument);

      if (
        isAwaitingCategorization({
          postedFileType: pendingUpload.fileType,
          returnedFileType: uploadedDocument.file_type,
        })
      ) {
        trackDocumentProcessing(uploadedDocument.id);
      }

      finalizeBatch(pendingUpload.batchId);

      return uploadedDocument;
    } catch {
      if (isApplicationSwitchedSince(uploadGeneration)) {
        return null;
      }

      removePending();

      if (replacedDocumentId) {
        uploadFailures.value = [...uploadFailures.value, { kind: "replace", replacedDocumentId }];
      } else if (pendingUpload.fileType) {
        uploadFailures.value = [
          ...uploadFailures.value,
          { kind: "upload", fileType: pendingUpload.fileType, batchSequence },
        ];
      }

      toast.value.addMessage({
        title: t("documents.toasts.uploadFailed", { name: pendingUpload.fileName }),
        type: "error",
      });

      batch?.failed.push(pendingUpload);
      finalizeBatch(pendingUpload.batchId);

      return null;
    }
  };

  const { executeFetch: uploadDocuments, loading: uploadDocumentsIsLoading } = usePromiseWrapper(
    async ({ files, documentOptions, uploadSource, replacedDocumentId }: UploadDocumentsParams) => {
      const uploadApplicationId = applicationId.value;
      const uploadGeneration = applicationGeneration;

      if (!uploadApplicationId) {
        return [];
      }

      const uploadableFiles = filterUploadableFiles(Array.from(files));

      if (!uploadableFiles.length) {
        return [];
      }

      const batchId = makeRandomString();
      latestBatchSequence += 1;
      const batchSequence = latestBatchSequence;
      const postedFileType = documentOptions?.file_type ?? undefined;

      const isRetry = clearUploadFailures((failure) => {
        if (failure.kind === "replace") {
          return failure.replacedDocumentId === replacedDocumentId;
        }

        return !replacedDocumentId && failure.fileType === postedFileType;
      });

      if (isRetry) {
        analytics.trackEvent(AnalyticsEvent.DocumentUploadRetried, {
          application_id: applicationId.value,
          file_type: postedFileType,
        });
      }

      trackedBatches.set(batchId, {
        uploaded: [],
        failed: [],
        uploadSource: uploadSource ?? "click",
      });

      const uploads = uploadableFiles.map((file) => {
        const pendingUpload: PendingUpload = {
          id: makeRandomString(),
          batchId,
          fileName: file.name,
          fileSize: file.size,
          file,
          fileType: postedFileType,
          offerStipId: documentOptions?.offer_stip_id ?? undefined,
        };

        pendingUploads.value = [...pendingUploads.value, pendingUpload];

        return uploadSingleFile({
          uploadApplicationId,
          uploadGeneration,
          batchSequence,
          pendingUpload,
          documentOptions,
          replacedDocumentId,
        });
      });

      const results = await Promise.all(uploads);
      await refreshRequiredDocuments();

      return results.filter((result) => result !== null);
    },
    { onError: () => defaultErrorHandler("upload") }
  );

  const { executeFetch: updateDocument } = usePromiseWrapper(
    async (documentId: Document["id"], updateDocumentPayload: UpdateDocumentPayload) => {
      if (!applicationId.value) {
        return;
      }

      try {
        const result = await new ApplicationsHttpService().updateDocument(
          applicationId.value,
          documentId,
          updateDocumentPayload
        );
        await refreshRequiredDocuments();

        setUploadedDocuments(
          uploadedDocuments.value.map((document) =>
            document.id === documentId ? result : document
          )
        );

        return result;
      } catch {
        defaultErrorHandler("update");
      }
    }
  );

  const { executeFetch: replaceDocument } = usePromiseWrapper(
    async ({ documentId, files, documentOptions, uploadSource }: ReplaceDocumentParams) => {
      const replaceApplicationId = applicationId.value;
      const replaceGeneration = applicationGeneration;

      if (!replaceApplicationId) {
        return;
      }

      const uploaded = await uploadDocuments({
        files,
        documentOptions,
        uploadSource,
        replacedDocumentId: documentId,
      });

      if (!uploaded?.length || isApplicationSwitchedSince(replaceGeneration)) {
        return;
      }

      analytics.trackEvent(AnalyticsEvent.DocumentReplaced, { document_id: documentId });

      try {
        await new ApplicationsHttpService().deleteDocuments(replaceApplicationId, [documentId]);
        await refreshRequiredDocuments();

        setUploadedDocuments(
          uploadedDocuments.value.filter((document) => document.id !== documentId)
        );
      } catch {
        defaultErrorHandler("delete");
      }
    }
  );

  const uploadErrors = computed(() => {
    const byFileType: Record<Document["file_type"], string> = {};
    const byDocumentId: Record<Document["id"], string> = {};
    const message = t("documents.inline.uploadFailed");

    for (const failure of uploadFailures.value) {
      if (failure.kind === "replace") {
        byDocumentId[failure.replacedDocumentId] = message;
        continue;
      }

      byFileType[failure.fileType] = message;
    }

    return { byFileType, byDocumentId };
  });

  const documentRows = computed(() =>
    buildDocumentRows({
      requiredDocuments: requiredDocuments.value,
      uploadedDocuments: uploadedDocuments.value,
      pendingUploads: pendingUploads.value,
      processingDocumentIds: processingDocumentIds.value,
      uploadErrors: uploadErrors.value.byFileType,
      replaceErrors: uploadErrors.value.byDocumentId,
    })
  );

  const documentsNeedingActionCount = computed(
    () =>
      uploadedDocuments.value.filter(
        (document) =>
          deriveDocumentStatus({
            document,
            isProcessing: processingDocumentIds.value.has(document.id),
          }).status === "action_needed"
      ).length
  );

  const documentRowsForRequirements = (requirements: RequiredDocument[]) =>
    buildDocumentRows({
      requiredDocuments: requirements,
      uploadedDocuments: uploadedDocuments.value,
      pendingUploads: pendingUploads.value,
      processingDocumentIds: processingDocumentIds.value,
      uploadErrors: uploadErrors.value.byFileType,
      replaceErrors: uploadErrors.value.byDocumentId,
      isScopedToRequirements: true,
    });

  const documentsNeedingActionCountForStep = ({
    stepId,
    hasOwnStips,
  }: DocumentsNeedingActionCountForStepParams) =>
    countDocumentsNeedingAction(
      documentRowsForRequirements(
        requiredDocumentsForCheckoutStep({
          requiredDocuments: checkoutRequiredDocuments.value,
          stepId,
          hasOwnStips,
        })
      )
    );

  const isMissingRequiredDocuments = computed(() => {
    return requiredDocuments.value.some((requiredDocumentCollection) => {
      if (requiredDocumentCollection.is_optional) {
        return false;
      }

      return requiredDocumentCollection.required_count > requiredDocumentCollection.uploaded_count;
    });
  });

  watch(
    applicationId,
    () => {
      applicationGeneration += 1;
    },
    { flush: "sync" }
  );

  watch(processingDocumentIds, () => {
    for (const batchId of trackedBatches.keys()) {
      finalizeBatch(batchId);
    }
  });

  watch(
    () => selectedApplication.value?.id,
    (newSelectedApplicationId) => {
      setApplicationId(newSelectedApplicationId ?? null);
    },
    { immediate: true, flush: "sync" }
  );

  watch(
    () => applicationId.value,
    async (newId, oldId) => {
      if (newId !== oldId) {
        for (const timer of processingTimers.values()) {
          clearTimeout(timer);
        }
        processingTimers.clear();
        processingDocumentIds.value = new Set();
        pendingUploads.value = [];
        uploadFailures.value = [];
        trackedBatches.clear();
        documentBatchSequences.clear();
        setCheckoutRequiredDocuments([]);
      }

      if (!applicationId.value) {
        return;
      }

      await Promise.all([getUploadedDocuments(), refreshRequiredDocuments()]);
    },
    { immediate: true }
  );

  watch(
    applicationId,
    async (newId, oldId) => {
      if (!newId || !!oldId) {
        applicationChannel.value?.leave();
      }

      if (!newId || newId === oldId) {
        return;
      }

      applicationChannel.value = new ApplicationPrivateChannel(newId);
      applicationChannel.value?.subscribeTo(
        new CommentSavedForFileEvent((updatedComment) => {
          const documentToUpdate = uploadedDocuments.value.find(
            (doc) => doc.id === Number(updatedComment.commentable_id)
          );

          if (!documentToUpdate) {
            return;
          }

          documentToUpdate.comments ??= [];

          const commentIndex = documentToUpdate.comments.findIndex(
            ({ id }) => id === updatedComment.id
          );

          const commentIsVisibleToBorrower =
            updatedComment.is_shared_with_borrower &&
            !updatedComment.is_resolved &&
            Boolean(updatedComment.content?.items);
          const commentAlreadyExists = commentIndex !== -1;

          if (!commentAlreadyExists && commentIsVisibleToBorrower) {
            documentToUpdate.comments.push(updatedComment);
          } else if (commentAlreadyExists && !commentIsVisibleToBorrower) {
            documentToUpdate.comments.splice(commentIndex, 1);
          } else if (commentAlreadyExists && commentIsVisibleToBorrower) {
            documentToUpdate.comments[commentIndex] = updatedComment;
          }
        }),
        new FileTypeChangedEvent(async (updatedFile) => {
          const newFileType = updatedFile.file_type;

          if (!newFileType) {
            return;
          }

          resolveDocumentProcessing(updatedFile.id);

          const batchSequence = documentBatchSequences.get(updatedFile.id);

          if (batchSequence !== undefined) {
            clearSupersededUploadFailures({ fileType: newFileType, batchSequence });
          }

          setUploadedDocuments(
            uploadedDocuments.value.map((uploadedDocument) =>
              uploadedDocument.id === updatedFile.id
                ? {
                    ...uploadedDocument,
                    file_type: newFileType,
                  }
                : uploadedDocument
            )
          );

          await refreshRequiredDocuments();
        })
      );
    },
    { immediate: true }
  );

  return {
    checkoutRequiredDocuments,
    documentRows,
    documentRowsForRequirements,
    documentsNeedingActionCount,
    documentsNeedingActionCountForStep,
    isMissingRequiredDocuments,
    pendingUploads,
    processingDocumentIds,
    requiredDocuments,
    uploadDocumentsIsLoading,
    uploadedDocuments,
    uploadErrors,
    uploadFailures,
    replaceDocument,
    getCheckoutRequiredDocuments,
    getUploadedDocuments,
    refreshRequiredDocuments,
    resolveDocumentProcessing,
    setCheckoutRequiredDocuments,
    setRequiredDocuments,
    setUploadedDocuments,
    trackDocumentProcessing,
    uploadDocuments,
    updateDocument,
  };
});
